import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import type {
  IDashboardUser,
  IDashboardProject,
  IProjectsPage,
  IDashboardColumn,
  IWorkflowTask,
  ICriticalTask,
  ICompany,
  IContact,
  IMfgUser,
  IMarketType,
  IDepartment,
  IPermissions,
  ICustomOrderBody,
  FilterMap,
} from 'models';
import type { IMfg } from 'models';

// CMS / mfg ids are hardcoded to 1, version v1.0.0 (matches the Backbone source).
const CMS_ID = 1;
const CMS_VERSION = 'v1.0.0';
const MFG_ID = 1;

/**
 * Build a query string from a filter map, omitting empty values (§4: "empty
 * values omitted"). Returns the string WITHOUT a leading separator.
 */
const filtersToQuery = (filters?: FilterMap): string => {
  if (!filters) return '';
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') params.append(key, String(value));
  });
  return params.toString();
};

/**
 * RTK Query api for the dashboard. Reuses the shared `baseQuery` (CSRF +
 * X-Requested-With + 401→/login). Endpoints, URLs, params and CSRF match
 * §4/§6 of PHASE2_DASHBOARD_INVENTORY.md exactly.
 */
export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery,
  tagTypes: [
    'User',
    'Permissions',
    'Columns',
    'Projects',
    'PinnedProjects',
    'WorkflowTasks',
    'CriticalTask',
    'Companies',
    'Contacts',
    'Partners',
    'MfgUsers',
    'MarketTypes',
    'Departments',
    'Mfg',
  ],
  endpoints: (builder) => ({
    // ---- User ----
    getCurrentUser: builder.query<IDashboardUser, number>({
      query: (id) => `/api/v1.0/users/${id}`,
      providesTags: ['User'],
    }),
    updateUser: builder.mutation<
      IDashboardUser,
      { id: number; body: Partial<IDashboardUser> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/users/${id}`,
        method: 'PATCH',
        body,
      }),
      // The first drag-reorder PATCHes dashboard_projects_default_order to
      // 'manual' BEFORE persisting the order — legacy (whiteboard
      // view/projects.js:328-339) does not refetch the collections there, and
      // refetching would race the custom_order POST (and flash the table
      // preloader, QA #4). Column sorts ('field'/'-field') DO re-render with a
      // collection fetch (container.js:275-282), so those keep invalidating.
      invalidatesTags: (_result, _error, { body }) =>
        body.dashboard_projects_default_order === 'manual'
          ? ['User']
          : ['User', 'Projects', 'PinnedProjects'],
    }),

    // Edit-profile PATCH (same endpoint as updateUser) — legacy
    // edit_profile.js:95-108 saves the user model and only updates the header
    // initials; it does NOT refetch the dashboard collections, so this variant
    // invalidates only the User tag (no table preloader after Done).
    // The 200 body is the serialized user + a `redirect` flag (true when the
    // user changed their own password → legacy reloads the page).
    updateProfile: builder.mutation<
      IDashboardUser & { redirect?: boolean },
      { id: number; body: Partial<IDashboardUser> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/users/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    // ---- Permissions ----
    getPermissions: builder.query<IPermissions, void>({
      query: () => '/api/v1.0/users/permissions/',
      providesTags: ['Permissions'],
    }),

    // ---- Columns ----
    getColumns: builder.query<IDashboardColumn[], void>({
      query: () => '/api/v1.0/projects/columns/',
      providesTags: ['Columns'],
    }),

    // ---- Projects (main / un-pinned, paginated) ----
    // Response is a PLAIN ARRAY; total comes from each row's `count` field.
    getProjects: builder.query<IProjectsPage, { page?: number; filters?: FilterMap }>({
      query: ({ page = 1, filters }) => {
        const filterQs = filtersToQuery(filters);
        return `/api/v1.0/projects/?page=${page}${filterQs ? `&${filterQs}` : ''}`;
      },
      transformResponse: (rows: IDashboardProject[]): IProjectsPage => ({
        rows: rows ?? [],
        total: rows?.[0]?.count ?? 0,
      }),
      providesTags: ['Projects'],
    }),

    // ---- Pinned projects (TRIPLE-N 'pinnned=true', no page param) ----
    getPinnedProjects: builder.query<IProjectsPage, { filters?: FilterMap }>({
      query: ({ filters } = {}) => {
        const filterQs = filtersToQuery(filters);
        return `/api/v1.0/projects/?pinnned=true${filterQs ? `&${filterQs}` : ''}`;
      },
      transformResponse: (rows: IDashboardProject[]): IProjectsPage => ({
        rows: rows ?? [],
        total: rows?.[0]?.count ?? 0,
      }),
      providesTags: ['PinnedProjects'],
    }),

    createProject: builder.mutation<IDashboardProject, { name: string }>({
      query: (body) => ({
        url: '/api/v1.0/projects/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects', 'PinnedProjects'],
    }),

    renameProject: builder.mutation<IDashboardProject, { id: number; name: string }>({
      query: ({ id, name }) => ({
        url: `/api/v1.0/projects/${id}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Projects', 'PinnedProjects'],
    }),

    deleteProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1.0/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects', 'PinnedProjects'],
    }),

    // Persist pin/unpin + manual drag-reorder. Note the §4 header casing
    // oddity (X-Csrftoken) for the pin path; baseQuery already sets X-CSRFToken,
    // which the Django CSRF middleware accepts case-insensitively.
    //
    // Pin/unpin (entries carrying `dashboard_pinned`) refetches both lists —
    // the row must move between them (parity 67/68). A pure drag-reorder
    // (`dashboard_order`-only entries) does NOT: legacy reorder_projects
    // (whiteboard view/projects.js:305-346) saves the order and only re-renders
    // the infobar — no collection fetch, no table preloader (QA #4). The client
    // already renders the new order (useRowDragReorder keeps it locally).
    setCustomOrder: builder.mutation<void, ICustomOrderBody>({
      query: (body) => ({
        url: '/api/v1.0/projects/custom_order/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, body) =>
        body.project_custom_order.some((entry) => 'dashboard_pinned' in entry)
          ? ['Projects', 'PinnedProjects']
          : [],
    }),

    // Duplicate is a Django view (NOT DRF): form-encoded body to /project/duplicate.
    duplicateProject: builder.mutation<
      unknown,
      { project_id: number; project_name: string }
    >({
      query: ({ project_id, project_name }) => {
        const body = new URLSearchParams();
        body.append('project_id', String(project_id));
        body.append('project_name', project_name);
        return {
          url: '/project/duplicate',
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        };
      },
      invalidatesTags: ['Projects', 'PinnedProjects'],
    }),

    // ---- Workflow / critical task ----
    getWorkflowTasks: builder.query<IWorkflowTask[], void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/workflow/tasks/`,
      providesTags: ['WorkflowTasks'],
    }),
    getCriticalTask: builder.query<ICriticalTask, void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/workflow/critical_tasks/`,
      providesTags: ['CriticalTask'],
    }),

    // ---- Lookups (fetched once, cached/deduped by RTK Query) ----
    getCompanies: builder.query<ICompany[], { project_id?: number } | void>({
      query: (arg) =>
        arg && arg.project_id
          ? `/api/v1.0/projects/companies/?project_id=${arg.project_id}`
          : '/api/v1.0/projects/companies/',
      providesTags: ['Companies'],
    }),
    getContacts: builder.query<IContact[], { company_id?: number } | void>({
      query: (arg) =>
        arg && arg.company_id
          ? `/api/v1.0/projects/contacts/?company_id=${arg.company_id}`
          : '/api/v1.0/projects/contacts/',
      providesTags: ['Contacts'],
    }),
    getPartners: builder.query<ICompany[], void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/mfg/partners/`,
      providesTags: ['Partners'],
    }),
    getMfgUsers: builder.query<IMfgUser[], void>({
      query: () => `/api/v1.0/mfg/${MFG_ID}/users/`,
      providesTags: ['MfgUsers'],
    }),
    getMarketTypes: builder.query<IMarketType[], void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/mfg/market_types/`,
      providesTags: ['MarketTypes'],
    }),
    // Departments (edit-profile popup resolves user.department_id → name,
    // legacy collection/departments.js).
    getDepartments: builder.query<IDepartment[], void>({
      query: () => '/api/v1.0/company/departments/',
      providesTags: ['Departments'],
    }),
    getMfg: builder.query<IMfg, number>({
      query: (id) => `/api/v1.0/mfg/${id}`,
      providesTags: ['Mfg'],
    }),
  }),
});

export const {
  // Aliased to avoid colliding with authApi's `useGetCurrentUserQuery` when both
  // are re-exported from the `app/services` barrel. The dashboard fetches the
  // user by id (/users/{id}); auth uses /users/get_current/.
  useGetCurrentUserQuery: useGetDashboardUserQuery,
  useUpdateUserMutation,
  useUpdateProfileMutation,
  useGetPermissionsQuery,
  useGetColumnsQuery,
  useGetProjectsQuery,
  useGetPinnedProjectsQuery,
  useCreateProjectMutation,
  useRenameProjectMutation,
  useDeleteProjectMutation,
  useSetCustomOrderMutation,
  useDuplicateProjectMutation,
  useGetWorkflowTasksQuery,
  useGetCriticalTaskQuery,
  useGetCompaniesQuery,
  useGetContactsQuery,
  useGetPartnersQuery,
  useGetMfgUsersQuery,
  useGetMarketTypesQuery,
  useGetDepartmentsQuery,
  useGetMfgQuery: useGetDashboardMfgQuery,
} = dashboardApi;
